// vpn-web-bo/src/pages/accounting/usage.tsx
//
// VPN-76 流量使用明細頁（每用戶 upload/download/total + 連過節點 + node/region/pool 篩選）
// + VPN-77 應用場景分析分頁（各類占比 + Top 服務 + Unknown% + confidence 分布）。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  CloudServerOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Link, useSearchParams } from "react-router";
import { authFetch } from "../../utils/api";
import { BrandScopeNotice } from "../../components/BrandScopeNotice";
import { getBrandLabel, tokenManager } from "../../utils/tokenManager";
import { getViewScopeBrandForRequest, useViewScope } from "../../utils/viewScope";
import {
  formatBytes,
  downloadAccountingCsv,
  describeServiceCategory,
  describeConfidence,
  describeTrafficDeliveryDisposition,
  describeTrafficAttributionReason,
  canViewTrafficDeliveryStatus,
  canViewTrafficAttributionIntegrity,
  createLatestRequestGuard,
  defaultAccountingUsageDateRange,
  normalizeTrafficDeliveryStatusResponse,
  normalizeAttributionIntegrityResponse,
  visibleUsageRows,
  SERVICE_CATEGORIES,
  unwrap,
  type ServiceCategory,
  type AccountingUsageRow,
  type AccountingUsageNode,
  type AccountingUsageResponse,
  resolveUsageDeepLinkBrand,
  type TrafficServiceRow,
  type TrafficServicesResponse,
  type TrafficAttributionIntegrityRow,
  type TrafficAttributionIntegrityResponse,
  type TrafficDeliveryStatusRow,
  type TrafficDeliveryStatusResponse,
  type TrafficActivityRow,
  type TrafficActivitiesResponse,
  type TrafficClassificationCandidateRow,
  type TrafficClassificationCandidatesResponse,
  type TrafficClassifyCandidateResponse,
} from "./lib";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const API_PATH = "/api/admin/accounting/usage";
const TRAFFIC_SERVICES_PATH = "/api/admin/traffic/services";
const TRAFFIC_ACTIVITIES_PATH = "/api/admin/traffic/activities";
const TRAFFIC_CANDIDATES_PATH = "/api/admin/traffic/classification-candidates";
const TRAFFIC_CLASSIFY_CANDIDATE_PATH =
  '/api/admin/traffic/classification-candidates/classify';
const TRAFFIC_ATTRIBUTION_INTEGRITY_PATH =
  '/api/admin/traffic/attribution-integrity';
const TRAFFIC_DELIVERY_STATUS_PATH = '/api/admin/traffic/delivery-status';

const renderActivityDirection = (
  value: number | null,
  row: TrafficActivityRow,
) => {
  const formatted = value == null ? "—" : formatBytes(value);
  if (!row.has_total_only_usage) return formatted;
  return (
    <Tooltip title="此日含舊版僅回報總流量的資料；此欄為已收到的方向流量，未必等於總流量。">
      <span>{formatted} <Tag color="gold">部分</Tag></span>
    </Tooltip>
  );
};

/** 節點池（對齊節點頁 nodeCategory：primary/backup/overseas/internal）。 */
const NODE_POOL_OPTIONS = [
  { label: "不限", value: "" },
  { label: "主用節點 (primary)", value: "primary" },
  { label: "備援節點 (backup)", value: "backup" },
  { label: "海外節點 (overseas)", value: "overseas" },
  { label: "內部節點 (internal)", value: "internal" },
];

/* ───────── 連線節點清單 cell ───────── */
const NodeListCell: React.FC<{ nodes: AccountingUsageNode[] }> = ({
  nodes,
}) => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return <span style={{ color: "#999" }}>—</span>;
  }
  const shown = nodes.slice(0, 4);
  const rest = nodes.length - shown.length;
  return (
    <Space size={4} wrap>
      {shown.map((n, idx) => (
        <Tooltip
          key={`${n.node_id}-${idx}`}
          title={
            <div>
              <div>節點 ID：{n.node_id}</div>
              <div>節點名稱：{n.node_region || "-"}</div>
              <div>
                連線於：
                {n.connected_at
                  ? dayjs(n.connected_at).format("YYYY-MM-DD HH:mm")
                  : "-"}
              </div>
            </div>
          }
        >
          <Tag icon={<CloudServerOutlined />} style={{ margin: 0 }}>
            {n.node_region || `#${n.node_id}`}
          </Tag>
        </Tooltip>
      ))}
      {rest > 0 && (
        <Tooltip
          title={nodes
            .slice(4)
            .map((n) => `${n.node_region || "#" + n.node_id}`)
            .join("、")}
        >
          <Tag style={{ margin: 0 }}>+{rest}</Tag>
        </Tooltip>
      )}
    </Space>
  );
};

/* ───────── 預留欄位 cell（未來版本才於此層級收集） ───────── */
const ReservedFieldCell: React.FC<{ value: string | boolean | null }> = ({
  value,
}) => {
  if (value === null || value === undefined || value === "") {
    return (
      <Tooltip title="此欄位為預留欄位：後續版本才會在每用戶彙總層級收集，目前無值。">
        <Tag color="default" style={{ opacity: 0.6 }}>
          預留
        </Tag>
      </Tooltip>
    );
  }
  if (typeof value === "boolean") {
    return value ? <Tag color="gold">是</Tag> : <Tag>否</Tag>;
  }
  return <span>{value}</span>;
};

/* ───────── 應用場景 cell（已上線，導向「應用場景分析」分頁） ───────── */
const ScenarioHintCell: React.FC = () => (
  <Tooltip title="應用場景分類已上線，但在「應用場景分析」分頁以服務 / 類別維度呈現（此每用戶彙總層級不逐列細分）。">
    <Text type="secondary" style={{ fontSize: 12 }}>
      見「應用場景分析」
    </Text>
  </Tooltip>
);

/* ───────── VPN-76 流量明細分頁 ───────── */
const UsageDetailTab: React.FC = () => {
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState<AccountingUsageRow[]>([]);
  const [loadedGroupBy, setLoadedGroupBy] = useState<"user" | "day" | null>(
    null,
  );
  // Keep the query scope with the rows.  The page stays mounted when the
  // header changes brand, so rows from the previous scope must not render.
  const [loadedBrandScope, setLoadedBrandScope] = useState<string | null>(
    null,
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const latestRequestGuard = useRef(createLatestRequestGuard());

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // VPN-76：彙總方式（user=每用戶 / day=按日）。
  const [groupBy, setGroupBy] = useState<"user" | "day">("user");
  // 後端回報 device 篩選是否為 user 級近似（每用戶模式為 per-device 精確 → false；
  // 按日模式無 device 維度 → true），用於顯示提示。
  const [deviceApprox, setDeviceApprox] = useState(false);

  // 從 VPN-74 列點擊帶入的預設篩選
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(() => {
    const range = defaultAccountingUsageDateRange();
    return [dayjs(range.dateFrom), dayjs(range.dateTo)];
  });
  const [memberAccountId, setMemberAccountId] = useState(
    searchParams.get("member_account_id") ?? "",
  );
  const [deviceId, setDeviceId] = useState(searchParams.get("device_id") ?? "");
  // 檢視範圍改由全域 Header（useViewScope）驅動。
  const { scope: brandScope } = useViewScope();

  // VPN-76 精確維度篩選（任一給定 → 後端走 traffic 管線、per-node 精確）。
  const [nodeId, setNodeId] = useState(searchParams.get("node_id") ?? "");
  const [region, setRegion] = useState(searchParams.get("region") ?? "");
  const [nodePool, setNodePool] = useState(searchParams.get("node_pool") ?? "");

  // 任一精確維度給定 → 精確管線（device_filter_approximate=false）。
  const preciseDimensionActive =
    nodeId.trim() !== "" || region.trim() !== "" || nodePool.trim() !== "";

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (dateRange) {
      params.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
      params.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
    }
    if (memberAccountId.trim()) {
      params.set("member_account_id", memberAccountId.trim());
    }
    if (deviceId.trim()) params.set("device_id", deviceId.trim());
    if (nodeId.trim()) params.set("node_id", nodeId.trim());
    if (region.trim()) params.set("region", region.trim());
    if (nodePool.trim()) params.set("node_pool", nodePool.trim());
    if (groupBy === "day") params.set("groupBy", "day");
    params.set("brand", brandScope);
    return params;
  }, [
    page,
    pageSize,
    dateRange,
    memberAccountId,
    deviceId,
    nodeId,
    region,
    nodePool,
    groupBy,
    brandScope,
  ]);

  const fetchData = useCallback(async () => {
    const requestId = latestRequestGuard.current.begin();
    setLoading(true);
    try {
      const res = await authFetch(`${API_PATH}?${buildParams().toString()}`);
      const result = await res.json();
      if (!latestRequestGuard.current.isCurrent(requestId)) return;
      const body = unwrap<AccountingUsageResponse>(result);
      if (body) {
        setData(Array.isArray(body.rows) ? body.rows : []);
        setLoadedGroupBy(groupBy);
        setLoadedBrandScope(brandScope);
        setTotal(Number(body.total) || 0);
        setDeviceApprox(Boolean(body.device_filter_approximate));
      } else {
        message.error(result?.msg || "載入流量明細失敗");
      }
    } catch {
      if (!latestRequestGuard.current.isCurrent(requestId)) return;
      message.error("載入流量明細失敗");
    } finally {
      if (latestRequestGuard.current.isCurrent(requestId)) {
        setLoading(false);
      }
    }
  }, [buildParams, message, refreshVersion]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 檢視範圍變更時回到第一頁。
  useEffect(() => {
    // The request below will load the new scope.  Clear view metadata now
    // so a failed or slow request can never leave the previous brand visible.
    setLoadedBrandScope(null);
    setTotal(0);
    setDeviceApprox(false);
    setPage(1);
  }, [brandScope]);

  const handleQuery = () => {
    setPage(1);
    setRefreshVersion((version) => version + 1);
  };

  const handleReset = () => {
    const range = defaultAccountingUsageDateRange();
    setDateRange([dayjs(range.dateFrom), dayjs(range.dateTo)]);
    setMemberAccountId("");
    setDeviceId("");
    setNodeId("");
    setRegion("");
    setNodePool("");
    setDeviceApprox(false);
    setPage(1);
  };

  // VPN-76 本日 / 本月快捷：設定日期範圍即觸發查詢（buildParams 依賴 dateRange）。
  const applyDateShortcut = (unit: "day" | "month") => {
    setDateRange([dayjs().startOf(unit), dayjs().endOf(unit)]);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      params.delete("page");
      params.delete("pageSize");
      await downloadAccountingCsv(API_PATH, params, "accounting-usage");
      message.success("已開始下載 CSV");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "匯出失敗");
    } finally {
      setExporting(false);
    }
  };

  const userColumns: NonNullable<TableProps<AccountingUsageRow>["columns"]> = [
    {
      dataIndex: "member_account_id",
      title: "會員帳戶 ID",
      width: 120,
      fixed: "left",
      render: (memberAccountId: AccountingUsageRow["member_account_id"]) =>
        memberAccountId != null && memberAccountId !== "" ? (
          <Link
            to={`/users?memberAccountId=${encodeURIComponent(String(memberAccountId))}`}
            aria-label={`查看會員帳戶 ID ${memberAccountId}`}
          >
            {String(memberAccountId)}
          </Link>
        ) : (
          <span style={{ color: "#999" }}>未綁定會員</span>
        ),
    },
    {
      dataIndex: "user_id",
      title: "用戶 ID",
      width: 105,
      render: (clientId: AccountingUsageRow["user_id"]) =>
        clientId != null && clientId !== "" ? String(clientId) : "—",
    },
    {
      dataIndex: "brand",
      title: "品牌",
      width: 90,
      render: (brand: string) => <Tag color="blue">{getBrandLabel(brand)}</Tag>,
    },
    {
      dataIndex: "upload_bytes",
      title: "上傳",
      width: 120,
      align: "right",
      render: (v: number | null) => (v == null ? "—" : formatBytes(v)),
    },
    {
      dataIndex: "download_bytes",
      title: "下載",
      width: 120,
      align: "right",
      render: (v: number | null) => (v == null ? "—" : formatBytes(v)),
    },
    {
      dataIndex: "total_bytes",
      title: "總流量",
      width: 130,
      align: "right",
      render: (v: number) => <Text strong>{formatBytes(v)}</Text>,
    },
    {
      dataIndex: "nodes",
      title: "連過的節點",
      width: 240,
      render: (nodes: AccountingUsageNode[]) => <NodeListCell nodes={nodes} />,
    },
    {
      dataIndex: "node_pool",
      title: "節點池",
      width: 110,
      render: (v: string | null) => <ReservedFieldCell value={v} />,
    },
    {
      dataIndex: "quota_source",
      title: "額度來源",
      width: 110,
      render: (v: string | null) => <ReservedFieldCell value={v} />,
    },
    {
      dataIndex: "is_premium_quota",
      title: "付費額度",
      width: 100,
      render: (v: boolean | null) => <ReservedFieldCell value={v} />,
    },
    {
      dataIndex: "service_category",
      title: "應用場景",
      width: 120,
      render: () => <ScenarioHintCell />,
    },
  ];

  // VPN-76 按日彙總欄位（每 日期×品牌 一列；無單一 user / 節點維度）。
  const dayColumns: NonNullable<TableProps<AccountingUsageRow>["columns"]> = [
    {
      dataIndex: "bucket_date",
      title: "日期",
      width: 130,
      fixed: "left",
      render: (v: string | null) => v ?? "-",
    },
    {
      dataIndex: "brand",
      title: "品牌",
      width: 100,
      render: (brand: string) => <Tag color="blue">{getBrandLabel(brand)}</Tag>,
    },
    {
      dataIndex: "upload_bytes",
      title: "上傳",
      width: 140,
      align: "right",
      render: (v: number | null) => (v == null ? "—" : formatBytes(v)),
    },
    {
      dataIndex: "download_bytes",
      title: "下載",
      width: 140,
      align: "right",
      render: (v: number | null) => (v == null ? "—" : formatBytes(v)),
    },
    {
      dataIndex: "total_bytes",
      title: "總流量",
      width: 150,
      align: "right",
      render: (v: number) => <Text strong>{formatBytes(v)}</Text>,
    },
    {
      dataIndex: "has_total_only_usage",
      title: "資料說明",
      width: 180,
      render: (hasTotalOnlyUsage: boolean) =>
        hasTotalOnlyUsage ? (
          <Tooltip title="此日含舊版僅回報總流量的紀錄；上傳、下載僅統計有提供方向資料的紀錄。">
            <Tag color="gold">含舊版總量回報</Tag>
          </Tooltip>
        ) : (
          "—"
        ),
    },
  ];

  const columns = groupBy === "day" ? dayColumns : userColumns;
  const tableData = visibleUsageRows(
    data,
    loadedGroupBy,
    groupBy,
    loadedBrandScope,
    brandScope,
  );

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented
          value={groupBy}
          onChange={(v) => {
            setGroupBy(v as "user" | "day");
            setPage(1);
          }}
          options={[
            { label: "每用戶", value: "user" },
            { label: "按日", value: "day" },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
          }
          placeholder={["起日", "迄日"]}
        />
        <Button onClick={() => applyDateShortcut("day")}>本日</Button>
        <Button onClick={() => applyDateShortcut("month")}>本月</Button>
        <Text type="secondary">預設昨日～明日</Text>
        <Input
          placeholder="會員帳戶 ID"
          allowClear
          style={{ width: 140 }}
          value={memberAccountId}
          onChange={(e) => setMemberAccountId(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Tooltip title="依裝置精確篩選：顯示該裝置本身的實際流量（per-device 精確，非 user 級近似），涵蓋近 14 天明細保留窗。按日模式下無裝置維度，改為 user 級近似。">
          <Input
            placeholder="裝置 ID"
            allowClear
            style={{ width: 200 }}
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            onPressEnter={handleQuery}
          />
        </Tooltip>
        <Tooltip title="VPN-76：依節點 ID 篩選 → 切換為 per-node 精確管線（非 user 級近似）。">
          <Input
            placeholder="節點 ID"
            allowClear
            style={{ width: 110 }}
            value={nodeId}
            onChange={(e) => setNodeId(e.target.value)}
            onPressEnter={handleQuery}
          />
        </Tooltip>
        <Tooltip title="VPN-76：依節點地區（如 HK / AU）精確篩選。">
          <Input
            placeholder="地區（HK / AU…）"
            allowClear
            style={{ width: 140 }}
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            onPressEnter={handleQuery}
          />
        </Tooltip>
        <Tooltip title="VPN-76：依節點池（主用 / 備援 / 海外 / 內部）精確篩選。">
          <Select
            aria-label="節點池"
            placeholder="節點池"
            style={{ width: 170 }}
            value={nodePool || ""}
            options={NODE_POOL_OPTIONS}
            onChange={(v) => setNodePool(v)}
          />
        </Tooltip>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleQuery}
          loading={loading}
        >
          查詢
        </Button>
        <Button onClick={handleReset}>重設</Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
        >
          匯出 CSV
        </Button>
      </Space>

      {preciseDimensionActive && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="已切換為「節點精確管線」"
          description="目前依節點 ID / 地區 / 節點池篩選，資料來自 per-node 精確的 traffic 日彙總（非 user 級近似），數值對應指定節點的實際流量。"
        />
      )}

      {deviceApprox && !preciseDimensionActive && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="裝置篩選為「user 級近似」（按日模式）"
          description="按日彙總為時間序視角、無裝置維度，依裝置篩選時顯示的是該裝置所屬用戶的全部流量（含同一用戶的其他裝置）。切換到「每用戶」模式即為單一裝置精確流量（per-device）。"
        />
      )}

      {groupBy === "user" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="應用場景分析已上線（見上方「應用場景分析」分頁，含各類占比 / Top 服務 / 可信度）；本表的「節點池 / 額度來源 / 付費額度」為預留欄位，尚未於每用戶彙總層級收集。"
        />
      )}

      <Table
        rowKey={(r) =>
          groupBy === "day"
            ? `${r.bucket_date ?? ""}|${r.brand}`
            : `${r.user_id ?? ""}|${r.brand}`
        }
        dataSource={tableData}
        loading={loading}
        columns={columns}
        scroll={{ x: groupBy === "day" ? 840 : 1370 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

/** Member and legacy traffic have separate ledgers, so keep their audit view
 * out of the summary modes where it can be mistaken for a duplicated total. */
const TrafficActivityTab: React.FC = () => {
  const { message } = App.useApp();
  const isAllBrand = tokenManager.isAllBrandAdmin();
  const { scope: brandScope } = useViewScope();
  const latestRequestGuard = useRef(createLatestRequestGuard());
  const [activities, setActivities] = useState<TrafficActivityRow[]>([]);
  const [loadedBrandScope, setLoadedBrandScope] = useState<string | null>(
    null,
  );
  const [total, setTotal] = useState(0);
  const [defaultDateFrom, setDefaultDateFrom] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    () => {
      const range = defaultAccountingUsageDateRange();
      return [dayjs(range.dateFrom), dayjs(range.dateTo)];
    },
  );
  const [memberAccountId, setMemberAccountId] = useState("");
  const [nodeId, setNodeId] = useState("");

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: "20" });
    if (dateRange) {
      params.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
      params.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
    }
    if (memberAccountId.trim()) {
      params.set("member_account_id", memberAccountId.trim());
    }
    if (nodeId.trim()) params.set("node_id", nodeId.trim());
    if (isAllBrand) params.set("brand", brandScope);
    return params;
  }, [page, dateRange, memberAccountId, nodeId, isAllBrand, brandScope]);

  const fetchActivities = useCallback(async () => {
    const requestId = latestRequestGuard.current.begin();
    setLoading(true);
    try {
      const res = await authFetch(
        `${TRAFFIC_ACTIVITIES_PATH}?${buildParams().toString()}`,
      );
      const result = await res.json();
      if (!latestRequestGuard.current.isCurrent(requestId)) return;
      const body = unwrap<TrafficActivitiesResponse>(result);
      if (!body) {
        message.error(result?.msg || "載入流量活動失敗");
        return;
      }
      setActivities(Array.isArray(body.rows) ? body.rows : []);
      setLoadedBrandScope(brandScope);
      setTotal(Number(body.total) || 0);
      setDefaultDateFrom(body.applied_date_from ?? null);
    } catch {
      if (latestRequestGuard.current.isCurrent(requestId)) {
        message.error("載入流量活動失敗");
      }
    } finally {
      if (latestRequestGuard.current.isCurrent(requestId)) setLoading(false);
    }
  }, [buildParams, message]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);
  useEffect(() => {
    setLoadedBrandScope(null);
    setActivities([]);
    setTotal(0);
    setDefaultDateFrom(null);
    setPage(1);
  }, [brandScope]);

  const activityColumns: NonNullable<
    TableProps<TrafficActivityRow>["columns"]
  > = [
    { dataIndex: "stats_date", title: "日期", width: 110 },
    {
      dataIndex: "source",
      title: "來源",
      width: 110,
      render: (v: TrafficActivityRow["source"]) =>
        v === "member_usage_window" ? (
          <Tag color="purple">會員用量回報</Tag>
        ) : (
          <Tag>舊版連線紀錄</Tag>
        ),
    },
    {
      dataIndex: "member_account_id",
      title: "會員帳戶 ID",
      width: 130,
      render: (memberAccountId: TrafficActivityRow["member_account_id"]) =>
        memberAccountId != null ? (
          <Link
            to={`/users?memberAccountId=${encodeURIComponent(String(memberAccountId))}`}
            aria-label={`查看會員帳戶 ID ${memberAccountId}`}
          >
            {String(memberAccountId)}
          </Link>
        ) : (
          "—"
        ),
    },
    {
      dataIndex: "user_id",
      title: "用戶 ID",
      width: 105,
      render: (clientId: TrafficActivityRow["user_id"]) =>
        clientId != null ? String(clientId) : "—",
    },
    {
      dataIndex: "node_name",
      title: "節點",
      width: 150,
      render: (nodeName: string | null, row: TrafficActivityRow) =>
        nodeName ? nodeName : row.node_id == null ? "—" : `#${row.node_id}`,
    },
    {
      dataIndex: "upload_bytes",
      title: "上傳",
      width: 110,
      align: "right",
      render: (v: number | null, row: TrafficActivityRow) =>
        renderActivityDirection(v, row),
    },
    {
      dataIndex: "download_bytes",
      title: "下載",
      width: 110,
      align: "right",
      render: (v: number | null, row: TrafficActivityRow) =>
        renderActivityDirection(v, row),
    },
    {
      dataIndex: "total_bytes",
      title: "總流量",
      width: 130,
      align: "right",
      render: (v: number) => formatBytes(v),
    },
    {
      dataIndex: "report_count",
      title: "報告 / session",
      width: 100,
      align: "right",
      render: (v: number) => String(v ?? 0),
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="活動明細為獨立稽核視圖，請勿與「流量明細」的每用戶／按日彙總相加。"
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
            setPage(1);
          }}
          placeholder={["起日", "迄日"]}
        />
        <Input
          placeholder="會員帳戶 ID"
          allowClear
          style={{ width: 140 }}
          value={memberAccountId}
          onChange={(e) => setMemberAccountId(e.target.value)}
          onPressEnter={() => setPage(1)}
        />
        <Input
          placeholder="節點 ID"
          allowClear
          style={{ width: 110 }}
          value={nodeId}
          onChange={(e) => setNodeId(e.target.value)}
          onPressEnter={() => setPage(1)}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchActivities}>
          重新整理
        </Button>
      </Space>
      <Card
        size="small"
        title="會員／舊版流量活動"
        extra={
          <Tooltip title="已升級 reporter 的會員用量會顯示上傳與下載；舊版僅有總流量的報告會顯示 —。">
            <Text type="secondary" style={{ fontSize: 12 }}>
              會員帳號 × 節點日彙總
            </Text>
          </Tooltip>
        }
      >
        {defaultDateFrom && !dateRange && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message={`未指定日期，活動明細預設顯示自 ${defaultDateFrom} 起的 31 天資料`}
          />
        )}
        <Table
          rowKey={(r) =>
            `${r.stats_date ?? ""}|${r.source}|${r.principal_type}|${r.principal_id}|${r.brand}|${r.node_id ?? ""}`
          }
          dataSource={loadedBrandScope === brandScope ? activities : []}
          loading={loading}
          columns={activityColumns}
          size="small"
          scroll={{ x: 1065 }}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            showSizeChanger: false,
            onChange: (nextPage) => setPage(nextPage),
          }}
        />
      </Card>
    </div>
  );
};

/* ───────── Sidecar delivery projection（僅全品牌彙總） ───────── */

const formatDeliveryTimestamp = (value: string | null): string =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—';

/**
 * A deliberately separate operational tab: these records describe telemetry
 * delivery, not customer traffic totals.  It is mounted only for an
 * all-brand admin in the aggregate scope, and its callback has the same guard
 * so an in-flight scope/identity change cannot result in a later request.
 */
const TrafficDeliveryStatusTab: React.FC = () => {
  const isAllBrand = tokenManager.isAllBrandAdmin();
  const { scope: brandScope } = useViewScope();
  const canViewDeliveryStatus = canViewTrafficDeliveryStatus(
    isAllBrand,
    brandScope,
  );
  const requestId = useRef(0);
  const [status, setStatus] = useState<TrafficDeliveryStatusResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!canViewDeliveryStatus) return;
    const currentRequestId = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(TRAFFIC_DELIVERY_STATUS_PATH);
      const result = await response.json();
      const normalized = normalizeTrafficDeliveryStatusResponse(
        unwrap<unknown>(result),
      );
      if (currentRequestId !== requestId.current) return;
      if (!normalized) {
        setStatus(null);
        setError(result?.msg || '載入節點上報狀態失敗');
        return;
      }
      setStatus(normalized);
    } catch {
      if (currentRequestId !== requestId.current) return;
      setStatus(null);
      setError('載入節點上報狀態失敗');
    } finally {
      if (currentRequestId === requestId.current) setLoading(false);
    }
  }, [canViewDeliveryStatus]);

  useEffect(() => {
    if (canViewDeliveryStatus) {
      fetchStatus();
      return () => {
        // A scope switch removes this tab.  Invalidate its request so a late
        // response cannot update an unmounted cross-brand view.
        requestId.current += 1;
      };
    }
    // Cancel any old response and discard cross-brand operational data before
    // the component can render under a narrower identity/scope.
    requestId.current += 1;
    setStatus(null);
    setError(null);
    setLoading(false);
  }, [canViewDeliveryStatus, fetchStatus]);

  if (!canViewDeliveryStatus) return null;

  const columns: NonNullable<TableProps<TrafficDeliveryStatusRow>['columns']> = [
    {
      dataIndex: 'node_name',
      title: '節點',
      width: 170,
      render: (name: string | null, row: TrafficDeliveryStatusRow) =>
        name || `#${row.node_id}`,
    },
    {
      dataIndex: 'is_stale',
      title: '心跳狀態',
      width: 105,
      render: (isStale: boolean) =>
        isStale ? <Tag color="red">逾時</Tag> : <Tag color="green">正常</Tag>,
    },
    {
      dataIndex: 'last_received_at',
      title: 'BO 最近收到',
      width: 175,
      render: formatDeliveryTimestamp,
    },
    {
      dataIndex: 'last_heartbeat_at',
      title: '最近心跳',
      width: 175,
      render: formatDeliveryTimestamp,
    },
    {
      dataIndex: 'last_detail_accepted_at',
      title: '最近接受明細',
      width: 175,
      render: formatDeliveryTimestamp,
    },
    {
      dataIndex: 'last_snapshot_at',
      title: '最近快照',
      width: 175,
      render: formatDeliveryTimestamp,
    },
    {
      dataIndex: 'sidecar_version',
      title: 'Sidecar 版本',
      width: 140,
      render: (version: string | null) => version ? <Text code>{version}</Text> : '—',
    },
    {
      key: 'queue',
      title: '待送佇列',
      width: 150,
      render: (_: unknown, row: TrafficDeliveryStatusRow) => (
        <Space direction="vertical" size={0}>
          <span>{row.queue_depth} 份報告</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatBytes(row.queued_bytes)}
          </Text>
        </Space>
      ),
    },
    {
      key: 'drops',
      title: '累積丟棄',
      width: 140,
      render: (_: unknown, row: TrafficDeliveryStatusRow) => {
        const hasDrop =
          row.dropped_report_count > 0 || row.dropped_record_count > 0;
        return (
          <Text type={hasDrop ? 'danger' : undefined}>
            {row.dropped_report_count} 報告 / {row.dropped_record_count} 筆
          </Text>
        );
      },
    },
    {
      dataIndex: 'last_disposition',
      title: '最近回執',
      width: 145,
      render: (disposition: TrafficDeliveryStatusRow['last_disposition']) => {
        const description = describeTrafficDeliveryDisposition(disposition);
        return <Tag color={description.color}>{description.label}</Tag>;
      },
    },
    {
      dataIndex: 'last_reject_reason',
      title: '最近拒收原因',
      width: 180,
      render: (reason: string | null) =>
        reason ? <Text code>{reason}</Text> : '—',
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="節點 telemetry 上報狀態"
        description="僅顯示由 BO 接收的安全節點狀態，不含帳號、目的地、IP、report ID 或任何憑證。逾時、佇列堆積、丟棄或拒收需在 UAT 發版前處理。"
      />
      <Card
        size="small"
        title="Sidecar 投遞健康度"
        extra={
          <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchStatus}>
            重新整理
          </Button>
        }
      >
        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            action={
              <Button size="small" onClick={fetchStatus}>
                重試
              </Button>
            }
          />
        ) : (
          <Table
            rowKey={(row) => row.node_id}
            dataSource={status?.rows ?? []}
            loading={loading}
            columns={columns}
            size="small"
            scroll={{ x: 1730 }}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="尚未收到任何節點的 telemetry heartbeat 或上報"
                />
              ),
            }}
          />
        )}
      </Card>
    </div>
  );
};

/* ───────── 各類占比進度條（依類別分組加總 service rows） ───────── */
interface CategoryShare {
  category: string;
  total_bytes: number;
  share_pct: number;
}

/**
 * service rows → 依 service_category 分組加總，回各類占比（降冪）。
 *
 * 占比口徑：加總後端回的 per-row `share_pct`（各 row 已對「全集合總流量」算好占比），
 * 不用「本類 bytes 和 / totalBytes」自算。因為 rows 只含 Top-N（後端截斷），
 * 若用截斷後的 bytes 當分子、全集合 totalBytes 當分母，服務數 > N 時各類占比會偏低、
 * 總和 < 100%，且與 Unknown 卡（後端 unknown_pct，同全集合分母）口徑不一致。
 * 直接加總 per-row share_pct 讓分子/分母同源，與 Unknown 口徑對齊。
 * total_bytes 僅用於顯示與排序（Top-N 之和，非全集合）。
 */
function aggregateByCategory(rows: TrafficServiceRow[]): CategoryShare[] {
  const bytesMap = new Map<string, number>();
  const shareMap = new Map<string, number>();
  for (const r of rows) {
    const cat = r.service_category || "unknown";
    bytesMap.set(cat, (bytesMap.get(cat) ?? 0) + Number(r.total_bytes || 0));
    shareMap.set(cat, (shareMap.get(cat) ?? 0) + Number(r.share_pct || 0));
  }
  return Array.from(bytesMap.entries())
    .map(([category, bytes]) => ({
      category,
      total_bytes: bytes,
      // per-row share_pct 加總後四捨五入到 1 位小數（後端每 row 已含全集合分母）
      share_pct: Math.round((shareMap.get(category) ?? 0) * 10) / 10,
    }))
    .sort((a, b) => b.total_bytes - a.total_bytes);
}

/** category → Progress strokeColor（沿用 Tag 色系的近似 hex）。 */
function categoryStroke(category: string): string {
  const map: Record<string, string> = {
    video: "#eb2f96",
    ai: "#2f54eb",
    social: "#13c2c2",
    browse: "#52c41a",
    download: "#faad14",
    game: "#fa541c",
    unknown: "#bfbfbf",
  };
  return map[category] ?? "#bfbfbf";
}

/* ───────── VPN-77 應用場景分析分頁 ───────── */
const ScenarioAnalysisTab: React.FC = () => {
  const { message } = App.useApp();
  const isAllBrand = tokenManager.isAllBrandAdmin();

  const [services, setServices] = useState<TrafficServiceRow[]>([]);
  const [totalBytes, setTotalBytes] = useState(0);
  const [unknownPct, setUnknownPct] = useState(0);
  const [confDist, setConfDist] = useState({
    high_pct: 0,
    medium_pct: 0,
    low_pct: 0,
  });
  const [memberCollection, setMemberCollection] = useState<
    TrafficServicesResponse["member_collection"] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // session 明細（依日期/user/node）
  const [sessions, setSessions] = useState<TrafficActivityRow[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPageSize, setSessionPageSize] = useState(20);
  const [candidates, setCandidates] = useState<TrafficClassificationCandidateRow[]>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateExporting, setCandidateExporting] = useState(false);
  const [classifyingKey, setClassifyingKey] = useState('');
  const [integrity, setIntegrity] =
    useState<TrafficAttributionIntegrityResponse | null>(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [integrityError, setIntegrityError] = useState<string | null>(null);
  // Each query owns a monotonic request id. Filter changes can overlap HTTP
  // requests; an older response must never overwrite the newer view.
  const servicesRequestId = useRef(0);
  const sessionsRequestId = useRef(0);
  const candidatesRequestId = useRef(0);
  const integrityRequestId = useRef(0);

  // 篩選（後端 /traffic/* 支援：date / user_id / node_id / brand）
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    () => {
      const range = defaultAccountingUsageDateRange();
      return [dayjs(range.dateFrom), dayjs(range.dateTo)];
    },
  );
  const [userId, setUserId] = useState("");
  const [nodeId, setNodeId] = useState("");
  // 檢視範圍改由全域 Header（useViewScope）驅動。
  const { scope: brandScope } = useViewScope();
  // Session rows are only renderable for the exact scope that produced them.
  // A brand switch re-renders before its replacement request resolves; never
  // show rows from the previous scope during that gap.
  const [loadedSessionScope, setLoadedSessionScope] = useState<
    typeof brandScope | null
  >(null);
  // 此 endpoint 是跨品牌完整性診斷：全品牌 admin 切到單一品牌視圖時也不可讀取。
  const canViewAttributionIntegrity = canViewTrafficAttributionIntegrity(
    isAllBrand,
    brandScope,
  );
  // category 為 client-side 篩選（Top 服務 list 用）。
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const buildBaseParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange) {
      params.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
      params.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
    }
    if (userId.trim()) params.set("user_id", userId.trim());
    if (nodeId.trim()) params.set("node_id", nodeId.trim());
    if (isAllBrand) params.set("brand", brandScope);
    return params;
  }, [dateRange, userId, nodeId, isAllBrand, brandScope]);

  // `user_id` is the user-facing identifier in both the service summary and
  // activity table. The API resolves it through the current member link for
  // member-native reports as well as filtering legacy sessions directly.
  const buildActivityParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange) {
      params.set("dateFrom", dateRange[0].format("YYYY-MM-DD"));
      params.set("dateTo", dateRange[1].format("YYYY-MM-DD"));
    }
    if (userId.trim()) params.set("user_id", userId.trim());
    if (nodeId.trim()) params.set("node_id", nodeId.trim());
    if (isAllBrand) params.set("brand", brandScope);
    return params;
  }, [dateRange, userId, nodeId, isAllBrand, brandScope]);

  const buildCandidateParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange) {
      params.set("dateFrom", dateRange[0].startOf("day").toISOString());
      params.set("dateTo", dateRange[1].endOf("day").toISOString());
    }
    if (isAllBrand) params.set("brand", brandScope);
    params.set("topN", "50");
    return params;
  }, [dateRange, isAllBrand, brandScope]);

  // integrity API 沒有 brand 或 user filter；只沿用它所支援的日期與節點條件。
  const buildIntegrityParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateRange) {
      params.set('dateFrom', dateRange[0].format('YYYY-MM-DD'));
      params.set('dateTo', dateRange[1].format('YYYY-MM-DD'));
    }
    if (nodeId.trim()) params.set('node_id', nodeId.trim());
    return params;
  }, [dateRange, nodeId]);

  const fetchServices = useCallback(async () => {
    const requestId = ++servicesRequestId.current;
    setLoading(true);
    try {
      const params = buildBaseParams();
      params.set("topN", "50");
      const res = await authFetch(
        `${TRAFFIC_SERVICES_PATH}?${params.toString()}`,
      );
      const result = await res.json();
      const body = unwrap<TrafficServicesResponse>(result);
      if (requestId !== servicesRequestId.current) return;
      if (body) {
        setServices(Array.isArray(body.rows) ? body.rows : []);
        setTotalBytes(Number(body.total_bytes) || 0);
        setUnknownPct(Number(body.unknown_pct) || 0);
        setConfDist(
          body.confidence_distribution ?? {
            high_pct: 0,
            medium_pct: 0,
            low_pct: 0,
          },
        );
        setMemberCollection(body.member_collection ?? null);
      } else {
        message.error(result?.msg || "載入應用場景分析失敗");
      }
    } catch {
      if (requestId !== servicesRequestId.current) return;
      message.error('載入應用場景分析失敗');
    } finally {
      if (requestId === servicesRequestId.current) setLoading(false);
    }
  }, [buildBaseParams, message]);

  const fetchSessions = useCallback(async () => {
    const requestId = ++sessionsRequestId.current;
    const requestScope = brandScope;
    setSessionLoading(true);
    try {
      const params = buildActivityParams();
      params.set("page", String(sessionPage));
      params.set("pageSize", String(sessionPageSize));
      const res = await authFetch(
        `${TRAFFIC_ACTIVITIES_PATH}?${params.toString()}`,
      );
      const result = await res.json();
      const body = unwrap<TrafficActivitiesResponse>(result);
      if (requestId !== sessionsRequestId.current) return;
      if (body) {
        setSessions(Array.isArray(body.rows) ? body.rows : []);
        setSessionTotal(Number(body.total) || 0);
        setLoadedSessionScope(requestScope);
      } else {
        message.error(result?.msg || "載入 session 明細失敗");
      }
    } catch {
      if (requestId !== sessionsRequestId.current) return;
      message.error('載入 session 明細失敗');
    } finally {
      if (requestId === sessionsRequestId.current) setSessionLoading(false);
    }
  }, [buildActivityParams, sessionPage, sessionPageSize, message, brandScope]);

  const fetchCandidates = useCallback(async () => {
    const requestId = ++candidatesRequestId.current;
    setCandidateLoading(true);
    try {
      const params = buildCandidateParams();
      const res = await authFetch(
        `${TRAFFIC_CANDIDATES_PATH}?${params.toString()}`,
      );
      const result = await res.json();
      const body = unwrap<TrafficClassificationCandidatesResponse>(result);
      if (requestId !== candidatesRequestId.current) return;
      if (body) {
        setCandidates(Array.isArray(body.rows) ? body.rows : []);
      } else {
        message.error(result?.msg || "載入待補分類候選失敗");
      }
    } catch {
      if (requestId !== candidatesRequestId.current) return;
      message.error('載入待補分類候選失敗');
    } finally {
      if (requestId === candidatesRequestId.current) setCandidateLoading(false);
    }
  }, [buildCandidateParams, message]);

  const fetchAttributionIntegrity = useCallback(async () => {
    if (!canViewAttributionIntegrity) return;
    const requestId = ++integrityRequestId.current;
    setIntegrityLoading(true);
    setIntegrityError(null);
    try {
      const res = await authFetch(
        `${TRAFFIC_ATTRIBUTION_INTEGRITY_PATH}?${buildIntegrityParams().toString()}`,
      );
      const result = await res.json();
      const body = unwrap<unknown>(result);
      const normalized = normalizeAttributionIntegrityResponse(body);
      if (requestId !== integrityRequestId.current) return;
      if (normalized) {
        setIntegrity(normalized);
      } else {
        setIntegrity(null);
        setIntegrityError(result?.msg || '載入未分配流量資料失敗');
      }
    } catch {
      if (requestId !== integrityRequestId.current) return;
      setIntegrity(null);
      setIntegrityError('載入未分配流量資料失敗');
    } finally {
      if (requestId === integrityRequestId.current) setIntegrityLoading(false);
    }
  }, [buildIntegrityParams, canViewAttributionIntegrity]);

  const handleClassifyCandidate = useCallback(
    async (
      row: TrafficClassificationCandidateRow,
      category: ServiceCategory,
    ) => {
      const key = `${row.brand}|${row.host}`;
      setClassifyingKey(key);
      try {
        const res = await authFetch(TRAFFIC_CLASSIFY_CANDIDATE_PATH, {
          method: "POST",
          body: JSON.stringify({
            host: row.host,
            brand: row.brand,
            category,
          }),
        });
        const result = await res.json();
        const body = unwrap<TrafficClassifyCandidateResponse>(result);
        if (!body) {
          message.error(result?.msg || "分類候選失敗");
          return;
        }
        setCandidates((prev) =>
          prev.filter((item) => `${item.brand}|${item.host}` !== key),
        );
        message.success(
          `已分類為${describeServiceCategory(category).label}，清除 ${
            Number(body.cleared_sample_count) || 0
          } 筆 sample`,
        );
        fetchCandidates();
      } catch {
        message.error("分類候選失敗");
      } finally {
        setClassifyingKey("");
      }
    },
    [fetchCandidates, message],
  );

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);
  useEffect(() => {
    if (canViewAttributionIntegrity) {
      fetchAttributionIntegrity();
      return;
    }
    // 切到任一品牌時取消舊回應並清掉跨品牌資料，且絕不觸發 integrity request。
    integrityRequestId.current += 1;
    setIntegrity(null);
    setIntegrityError(null);
    setIntegrityLoading(false);
  }, [canViewAttributionIntegrity, fetchAttributionIntegrity]);

  // 檢視範圍變更時 session 明細回到第一頁。
  useEffect(() => {
    setSessionPage(1);
    setLoadedSessionScope(null);
  }, [brandScope]);

  const handleQuery = () => {
    setSessionPage(1);
    fetchServices();
    fetchSessions();
    fetchCandidates();
    fetchAttributionIntegrity();
  };

  const handleReset = () => {
    const range = defaultAccountingUsageDateRange();
    setDateRange([dayjs(range.dateFrom), dayjs(range.dateTo)]);
    setUserId("");
    setNodeId("");
    setCategoryFilter("all");
    setCandidates([]);
    setSessionPage(1);
  };

  const handleExportServices = async () => {
    setExporting(true);
    try {
      await downloadAccountingCsv(
        TRAFFIC_SERVICES_PATH,
        buildBaseParams(),
        "traffic-services",
      );
      message.success("已開始下載 CSV");
    } catch (err) {
      message.error(err instanceof Error ? err.message : "匯出失敗");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCandidates = async () => {
    setCandidateExporting(true);
    try {
      await downloadAccountingCsv(
        TRAFFIC_CANDIDATES_PATH,
        buildCandidateParams(),
        'traffic-classification-candidates',
      );
      message.success('已開始下載 CSV');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '匯出失敗');
    } finally {
      setCandidateExporting(false);
    }
  };

  const categoryShares = useMemo(
    () => aggregateByCategory(services),
    [services],
  );

  const categoryOptions = useMemo(
    () => [
      { label: "全部類別", value: "all" },
      ...categoryShares.map((c) => ({
        label: describeServiceCategory(c.category).label,
        value: c.category,
      })),
    ],
    [categoryShares],
  );

  const candidateCategoryOptions = useMemo(
    () =>
      SERVICE_CATEGORIES.filter((category) => category !== "unknown").map(
        (category) => {
          const d = describeServiceCategory(category);
          return {
            label: d.label,
            value: category,
          };
        },
      ),
    [],
  );

  const filteredServices = useMemo(
    () =>
      categoryFilter === "all"
        ? services
        : services.filter((s) => s.service_category === categoryFilter),
    [services, categoryFilter],
  );

  const serviceColumns: NonNullable<TableProps<TrafficServiceRow>["columns"]> =
    [
      {
        dataIndex: "service_category",
        title: "類別",
        width: 100,
        render: (c: string) => {
          const d = describeServiceCategory(c);
          return <Tag color={d.color}>{d.label}</Tag>;
        },
      },
      {
        dataIndex: "service_name",
        title: "服務",
        width: 160,
        render: (v: string) => v || <span style={{ color: "#999" }}>—</span>,
      },
      {
        dataIndex: "upload_bytes",
        title: "上傳",
        width: 110,
        align: "right",
        render: (v: number) => formatBytes(v),
      },
      {
        dataIndex: "download_bytes",
        title: "下載",
        width: 110,
        align: "right",
        render: (v: number) => formatBytes(v),
      },
      {
        dataIndex: "total_bytes",
        title: "總流量",
        width: 120,
        align: "right",
        render: (v: number) => <Text strong>{formatBytes(v)}</Text>,
      },
      {
        dataIndex: "share_pct",
        title: "占比",
        width: 130,
        render: (v: number) => (
          <Progress
            percent={Number(v) || 0}
            size="small"
            format={(p) => `${p}%`}
            status="normal"
          />
        ),
      },
      {
        dataIndex: "session_count",
        title: "session",
        width: 90,
        align: "right",
        render: (v: number) => String(v ?? 0),
      },
      {
        dataIndex: "confidence",
        title: "可信度",
        width: 90,
        render: (v: number) => {
          const d = describeConfidence(v);
          return <Tag color={d.color}>{d.label}</Tag>;
        },
      },
    ];

  const sessionColumns: NonNullable<TableProps<TrafficActivityRow>["columns"]> =
    [
      {
        dataIndex: "stats_date",
        title: "日期",
        width: 120,
        render: (v: string | null) => v ?? "-",
      },
      {
        dataIndex: "brand",
        title: "品牌",
        width: 90,
        render: (b: string) => <Tag color="blue">{getBrandLabel(b)}</Tag>,
      },
      {
        dataIndex: "source",
        title: "來源",
        width: 100,
        render: (v: TrafficActivityRow["source"]) => (
          <Tag color={v === "member_usage_window" ? "purple" : "default"}>
            {v === "member_usage_window" ? "會員用量回報" : "舊版連線紀錄"}
          </Tag>
        ),
      },
      {
        dataIndex: "member_account_id",
        title: "會員帳戶 ID",
        width: 130,
        render: (memberAccountId: TrafficActivityRow["member_account_id"]) =>
          memberAccountId != null ? (
            <Link
              to={`/users?memberAccountId=${encodeURIComponent(String(memberAccountId))}`}
              aria-label={`查看會員帳戶 ID ${memberAccountId}`}
            >
              {String(memberAccountId)}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        dataIndex: "user_id",
        title: "用戶 ID",
        width: 105,
        render: (clientId: TrafficActivityRow["user_id"]) =>
          clientId != null ? String(clientId) : "—",
      },
      {
        dataIndex: "node_name",
        title: "節點",
        width: 150,
        render: (nodeName: string | null, row: TrafficActivityRow) =>
          nodeName ? nodeName : row.node_id == null ? "—" : `#${row.node_id}`,
      },
      {
        dataIndex: "upload_bytes",
        title: "上傳",
        width: 110,
        align: "right",
        render: (v: number | null, row: TrafficActivityRow) =>
          renderActivityDirection(v, row),
      },
      {
        dataIndex: "download_bytes",
        title: "下載",
        width: 110,
        align: "right",
        render: (v: number | null, row: TrafficActivityRow) =>
          renderActivityDirection(v, row),
      },
      {
        dataIndex: "total_bytes",
        title: "總流量",
        width: 120,
        align: "right",
        render: (v: number) => <Text strong>{formatBytes(v)}</Text>,
      },
      {
        dataIndex: "report_count",
        title: "報告 / session",
        width: 100,
        align: "right",
        render: (v: number) => String(v ?? 0),
      },
    ];

  const integrityColumns: NonNullable<
    TableProps<TrafficAttributionIntegrityRow>['columns']
  > = [
    {
      dataIndex: 'reason',
      title: '未分配原因',
      width: 180,
      render: (reason: TrafficAttributionIntegrityRow['reason']) => {
        const description = describeTrafficAttributionReason(reason);
        return <Tag color={description.color}>{description.label}</Tag>;
      },
    },
    {
      dataIndex: 'node_id',
      title: '節點',
      width: 130,
      render: (nodeIdValue: number | null) =>
        nodeIdValue == null ? '未辨識節點' : `#${nodeIdValue}`,
    },
    {
      dataIndex: 'upload_bytes',
      title: '上傳',
      width: 120,
      align: 'right',
      render: (value: number) => formatBytes(value),
    },
    {
      dataIndex: 'download_bytes',
      title: '下載',
      width: 120,
      align: 'right',
      render: (value: number) => formatBytes(value),
    },
    {
      dataIndex: 'total_bytes',
      title: '未分配流量',
      width: 140,
      align: 'right',
      render: (value: number) => <Text strong>{formatBytes(value)}</Text>,
    },
    {
      dataIndex: 'event_count',
      title: 'Session 數',
      width: 100,
      align: 'right',
      render: (value: number) => String(value),
    },
  ];

  const candidateColumns: NonNullable<
    TableProps<TrafficClassificationCandidateRow>["columns"]
  > = [
    {
      dataIndex: "host",
      title: "候選 domain",
      width: 220,
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      dataIndex: "brand",
      title: "品牌",
      width: 100,
      render: (b: string) => <Tag color="blue">{getBrandLabel(b)}</Tag>,
    },
    {
      dataIndex: "sample_count",
      title: "sample 數",
      width: 110,
      align: "right",
      render: (v: number) => <Text strong>{Number(v) || 0}</Text>,
    },
    {
      dataIndex: "first_sampled_at",
      title: "首次出現",
      width: 150,
      render: (v: string | null) =>
        v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      dataIndex: "last_sampled_at",
      title: "最近出現",
      width: 150,
      render: (v: string | null) =>
        v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      key: "actions",
      title: "分類",
      width: 140,
      fixed: "right",
      render: (_: unknown, row: TrafficClassificationCandidateRow) => {
        const key = `${row.brand}|${row.host}`;
        const loading = classifyingKey === key;
        return (
          <Select
            aria-label="候選分類"
            size="small"
            placeholder="選擇分類"
            style={{ width: 116 }}
            options={candidateCategoryOptions}
            loading={loading}
            disabled={Boolean(classifyingKey) && !loading}
            onChange={(category: ServiceCategory) =>
              handleClassifyCandidate(row, category)
            }
          />
        );
      },
    },
  ];

  const hasData =
    totalBytes > 0 ||
    services.length > 0 ||
    sessions.length > 0 ||
    candidates.length > 0 ||
    candidateLoading ||
    sessionLoading ||
    (canViewAttributionIntegrity &&
      (integrityLoading || integrityError !== null || integrity !== null));

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
          }
          placeholder={["起日", "迄日"]}
        />
        <Input
          placeholder="用戶 ID"
          allowClear
          style={{ width: 130 }}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Input
          placeholder="節點 ID"
          allowClear
          style={{ width: 110 }}
          value={nodeId}
          onChange={(e) => setNodeId(e.target.value)}
          onPressEnter={handleQuery}
        />
        <Tooltip title="類別篩選為 Top 服務 list 的前端篩選（不影響占比與統計）。">
          <Select
            aria-label="應用場景類別"
            style={{ width: 150 }}
            value={categoryFilter}
            options={categoryOptions}
            onChange={(v) => setCategoryFilter(v)}
          />
        </Tooltip>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleQuery}
          loading={loading}
        >
          查詢
        </Button>
        <Button onClick={handleReset}>重設</Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportServices}
          loading={exporting}
        >
          匯出服務分布 CSV
        </Button>
      </Space>

      {!hasData && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size={4}>
              <Text strong style={{ fontSize: 15 }}>
                此區間 / 篩選下沒有流量分類資料
              </Text>
              <Text type="secondary">
                請調整日期區間或篩選條件；節點需開啟分類管線後才會累積資料。
              </Text>
            </Space>
          }
          style={{ padding: "32px 0" }}
        />
      ) : (
        <>
          {canViewAttributionIntegrity && (
            <Card
              size="small"
              title="流量歸屬完整性"
              style={{ marginBottom: 12 }}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  僅全品牌彙總檢視 · 僅 Session 隔離事件
                </Text>
              }
            >
              {integrityLoading ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="正在載入未分配流量資料…"
                />
              ) : integrityError ? (
                <Alert type="error" showIcon message={integrityError} />
              ) : integrity ? (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="未分配流量不會計入任何品牌或用戶"
                    description="未分配流量與 Session 數只統計被隔離的 session；被隔離的 flow 僅保留作分類診斷，不計入此處總量或計數。僅涵蓋本功能部署後的新上報資料，並依目前的日期與節點篩選彙總；可查資料最多保留 14 天。"
                  />
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="未分配流量"
                          value={formatBytes(integrity.total_unattributed_bytes)}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="未分配 Session"
                          value={integrity.total_session_count}
                        />
                      </Card>
                    </Col>
                  </Row>
                  {integrity.rows.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="此條件下沒有未分配流量"
                    />
                  ) : (
                    <Table
                      rowKey={(row) => `${row.reason}|${row.node_id ?? 'none'}`}
                      dataSource={integrity.rows}
                      columns={integrityColumns}
                      size="small"
                      scroll={{ x: 790 }}
                      pagination={false}
                    />
                  )}
                </>
              ) : null}
            </Card>
          )}

          {memberCollection?.state === "unavailable" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="會員流量分類尚未啟用"
              description="會員 ticket-v2 的總流量可在下方「Session 日彙總明細」查看；應用場景分析目前只包含已分類的舊版 session，不會把會員用量誤列為應用場景。"
            />
          )}
          {/* 統計卡 */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="總流量" value={formatBytes(totalBytes)} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Unknown 占比"
                  value={unknownPct}
                  suffix="%"
                  valueStyle={{
                    color: unknownPct > 30 ? "#cf1322" : undefined,
                  }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="高可信度占比"
                  value={confDist.high_pct}
                  suffix="%"
                  valueStyle={{ color: "#3f8600" }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="低可信度占比"
                  value={confDist.low_pct}
                  suffix="%"
                  valueStyle={{
                    color: confDist.low_pct > 30 ? "#cf1322" : undefined,
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* 各類占比 */}
          <Card
            size="small"
            title="各類應用場景占比"
            style={{ marginBottom: 12 }}
            extra={
              <Tooltip title="confidence 分布：高 / 中 / 低 可信度的流量占比，反映分類規則命中強度。">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  可信度 高 {confDist.high_pct}% / 中 {confDist.medium_pct}% /
                  低 {confDist.low_pct}%
                </Text>
              </Tooltip>
            }
          >
            {categoryShares.length === 0 ? (
              <Text type="secondary">尚無資料</Text>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                {categoryShares.map((c) => {
                  const d = describeServiceCategory(c.category);
                  return (
                    <div key={c.category}>
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <Tag color={d.color}>{d.label}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatBytes(c.total_bytes)}（{c.share_pct}%）
                        </Text>
                      </Space>
                      <Progress
                        percent={c.share_pct}
                        showInfo={false}
                        strokeColor={categoryStroke(c.category)}
                        size="small"
                      />
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>

          {/* Top 服務 list */}
          <Card
            size="small"
            title="Top 服務（依總流量）"
            style={{ marginBottom: 12 }}
          >
            <Table
              rowKey={(r) => `${r.service_category}|${r.service_name}`}
              dataSource={filteredServices}
              loading={loading}
              columns={serviceColumns}
              size="small"
              scroll={{ x: 920 }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
            />
          </Card>

          <Card
            size="small"
            title="待補分類候選"
            style={{ marginBottom: 12 }}
            extra={
              <Space size={8}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  來源：72 小時 debug sample，sample 數不是流量
                </Text>
                <Button
                  icon={<DownloadOutlined />}
                  loading={candidateExporting}
                  onClick={handleExportCandidates}
                >
                  匯出 CSV
                </Button>
              </Space>
            }
          >
            <Table
              rowKey={(r) => `${r.brand}|${r.host}`}
              dataSource={candidates}
              loading={candidateLoading}
              columns={candidateColumns}
              size="small"
              scroll={{ x: 930 }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="目前沒有近期待補分類候選"
                  />
                ),
              }}
            />
          </Card>

          {/* Typed legacy/member traffic read projection. */}
          <Card
            size="small"
            title="Session 日彙總明細"
            extra={
              <Tooltip title="會員資料按會員帳號、節點與台北日期彙總；已升級 reporter 的會員用量會顯示上傳與下載。若混有舊版僅有總流量的報告，會保留已知方向並標示「部分」。">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  舊版連線紀錄／會員帳戶用量回報
                </Text>
              </Tooltip>
            }
          >
            <Table
              rowKey={(r) =>
                `${r.stats_date ?? ""}|${r.source}|${r.principal_type}|${r.principal_id}|${r.brand}|${r.node_id ?? ""}`
              }
              dataSource={
                loadedSessionScope === brandScope ? sessions : []
              }
              loading={sessionLoading}
              columns={sessionColumns}
              size="small"
              scroll={{ x: 1050 }}
              pagination={{
                current: sessionPage,
                pageSize: sessionPageSize,
                total: sessionTotal,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (t) => `共 ${t} 筆`,
                onChange: (p, ps) => {
                  if (ps !== sessionPageSize) {
                    setSessionPageSize(ps);
                    setSessionPage(1);
                    return;
                  }
                  setSessionPage(p);
                },
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export const AccountingUsagePage: React.FC = () => {
  const isAllBrand = tokenManager.isAllBrandAdmin();
  const { scope: brandScope, setScope } = useViewScope();
  const [searchParams] = useSearchParams();
  const [scopeInitialized, setScopeInitialized] = useState(false);
  const deepLink = resolveUsageDeepLinkBrand(searchParams, tokenManager.getAllowedBrands());
  const requestedBrand = deepLink.brand;
  const needsDeepLinkScope = deepLink.required;
  const invalidDeepLinkBrand = deepLink.error;
  useEffect(() => {
    if (!needsDeepLinkScope || invalidDeepLinkBrand || !requestedBrand) return;
    if (brandScope !== requestedBrand ||
      getViewScopeBrandForRequest() !== requestedBrand) {
      setScope(requestedBrand);
    }
    setScopeInitialized(true);
  }, [needsDeepLinkScope, invalidDeepLinkBrand, requestedBrand, brandScope, setScope]);
  const deepLinkReady = !needsDeepLinkScope || (
    !invalidDeepLinkBrand && scopeInitialized &&
    brandScope === requestedBrand &&
    getViewScopeBrandForRequest() === requestedBrand
  );
  const canViewDeliveryStatus = canViewTrafficDeliveryStatus(
    isAllBrand,
    brandScope,
  );

  if (invalidDeepLinkBrand) {
    return <Alert type="error" showIcon message="品牌檢視範圍無效或未獲授權，無法載入流量明細" />;
  }
  if (!deepLinkReady) {
    return <Alert type="info" message="正在確認品牌檢視範圍…" />;
  }

  return (
    <div className="page-enter">
      <Title level={4} style={{ marginBottom: 16 }}>
        流量使用明細
      </Title>

      <BrandScopeNotice kind="accounting" style={{ marginBottom: 16 }} />

      <Tabs
        defaultActiveKey="detail"
        items={[
          {
            key: "detail",
            label: "流量明細",
            children: <UsageDetailTab key={searchParams.toString()} />,
          },
          {
            key: "activity",
            label: "流量活動",
            children: <TrafficActivityTab />,
          },
          {
            key: "scenario",
            label: (
              <Space size={4}>
                <ExperimentOutlined />
                應用場景分析
              </Space>
            ),
            children: <ScenarioAnalysisTab />,
          },
          ...(canViewDeliveryStatus
            ? [
                {
                  key: 'delivery-status',
                  label: (
                    <Space size={4}>
                      <CloudServerOutlined />
                      節點上報狀態
                    </Space>
                  ),
                  children: <TrafficDeliveryStatusTab />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default AccountingUsagePage;
